import { useQuery, useMutation } from 'react-query'
import useCacheKeys from '../use-cache-keys'
import useCache from '../use-cache'
import { apiEndpoints, apiRequester } from '../../utilities/api'
import { useCallback, useState } from 'react'

const useLearningResourceMutations = ({
    resourceId,
    onSuccess = () => {},
    onError = () => {},
}) => {

    const [ resource, setResource ] = useState({})
    const cacheKeys = useCacheKeys()

    const { clearCache } = useCache({
        key: cacheKeys.LEARNING_RESOURCES,
        onReload: onSuccess,
    })

    const { isLoading } = useQuery(
        [cacheKeys.LEARNING_RESOURCES, resourceId],
        () => apiRequester.get(apiEndpoints.learningResource({ resourceId })),
        {
            enabled: Boolean(resourceId),
            onSuccess: setResource
        }
    )

    const [ deleteResource, { isLoading: isDeleting } ] = useMutation(
        (id) => apiRequester.delete(apiEndpoints.learningResource({ resourceId: id || resourceId })),
        {
            onSuccess: clearCache,
            onError,
        }
    )

    //  Pull out values so we can be lazy in the ui portion and still follow validation
    const {
        name,
        description,
        type,
        url,
        dimensions,
    } = resource

    const [ create, { isLoading: isCreating } ] = useMutation(
        (resource) => apiRequester.post(apiEndpoints.learningResources(), {
            name,
            description,
            type,
            url,
            dimensions,
        }),
        {
            onSuccess: clearCache,
            onError,
        }
    )

    const [ update, { isLoading: isUpdating } ] = useMutation(
        (resource) => apiRequester.put(apiEndpoints.learningResource({ resourceId }), {
            name,
            description,
            type,
            url,
            dimensions,
        }),
        {
            onSuccess: clearCache,
            onError,
        }
    )

    const saveResource = useCallback((resourceOverride) => {
        const thisResource = resourceOverride || resource
        if (thisResource.id) {
            update(thisResource)
        } else {
            create(thisResource)
        }
    }, [resource])

    return {
        isLoading,
        resource,
        setResource,
        isDeleting,
        deleteResource,
        isSaving: isCreating || isUpdating,
        saveResource,
    }
}

export default useLearningResourceMutations